import React from 'react';
import { Card, CardContent } from '../ui/Card';
import { formatCurrency } from '../../lib/utils';
import { FlameIcon, ClockIcon, CalendarIcon, TrendingUpIcon, ZapIcon, UsersIcon } from 'lucide-react';
import type { PeakDay, PeakHour, ChannelBreakdown } from '../../lib/metrics-hooks';
interface MetricsPeakInsightsProps {
  peakDays: PeakDay[];
  peakHours: PeakHour[];
  channelBreakdown: ChannelBreakdown;
  totalOrders: number;
  totalRevenue: number;
  averageTicket: number;
}
export function MetricsPeakInsights({
  peakDays,
  peakHours,
  channelBreakdown,
  totalOrders,
  totalRevenue,
  averageTicket
}: MetricsPeakInsightsProps) {
  const totalChannel = channelBreakdown.qr + channelBreakdown.waiter + channelBreakdown.other;
  const qrPct = totalChannel > 0 ? Math.round(channelBreakdown.qr / totalChannel * 100) : 0;
  const waiterPct = totalChannel > 0 ? Math.round(channelBreakdown.waiter / totalChannel * 100) : 0;
  return <div className="space-y-4" data-id="element-355">
      {/* Summary Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-356">
        <Card data-id="element-357">
          <CardContent className="p-5" data-id="element-358">
            <div className="flex items-start gap-3" data-id="element-359">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-360">
                <TrendingUpIcon className="w-5 h-5 text-emerald-600" data-id="element-361" />
              </div>
              <div className="min-w-0" data-id="element-362">
                <p className="text-sm text-gray-500 font-medium" data-id="element-363">
                  Ingresos Totales
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-364">
                  {formatCurrency(totalRevenue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-365">
          <CardContent className="p-5" data-id="element-366">
            <div className="flex items-start gap-3" data-id="element-367">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-368">
                <UsersIcon className="w-5 h-5 text-blue-600" data-id="element-369" />
              </div>
              <div className="min-w-0" data-id="element-370">
                <p className="text-sm text-gray-500 font-medium" data-id="element-371">
                  Total Órdenes
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5" data-id="element-372">
                  {totalOrders}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-373">
          <CardContent className="p-5" data-id="element-374">
            <div className="flex items-start gap-3" data-id="element-375">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-376">
                <ZapIcon className="w-5 h-5 text-purple-600" data-id="element-377" />
              </div>
              <div className="min-w-0" data-id="element-378">
                <p className="text-sm text-gray-500 font-medium" data-id="element-379">
                  Ticket Promedio
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-380">
                  {formatCurrency(averageTicket)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Peak Insights Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-381">
        {/* Peak Day */}
        <Card data-id="element-382">
          <CardContent className="p-5" data-id="element-383">
            <div className="flex items-center gap-2 mb-3" data-id="element-384">
              <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center" data-id="element-385">
                <FlameIcon className="w-4 h-4 text-red-500" data-id="element-386" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-387">
                Día Pico
              </span>
            </div>
            {peakDays.length > 0 ? <div className="space-y-2" data-id="element-388">
                {peakDays.map((day, i) => <div key={day.date} className="flex items-center justify-between" data-id="element-389">
                    <div className="flex items-center gap-2" data-id="element-390">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-391">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-392">{day.label}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-393">
                      {day.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-394">Sin datos</p>}
          </CardContent>
        </Card>

        {/* Peak Hour */}
        <Card data-id="element-395">
          <CardContent className="p-5" data-id="element-396">
            <div className="flex items-center gap-2 mb-3" data-id="element-397">
              <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center" data-id="element-398">
                <ClockIcon className="w-4 h-4 text-amber-600" data-id="element-399" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-400">
                Hora Pico
              </span>
            </div>
            {peakHours.length > 0 ? <div className="space-y-2" data-id="element-401">
                {peakHours.map((hour, i) => <div key={hour.hour} className="flex items-center justify-between" data-id="element-402">
                    <div className="flex items-center gap-2" data-id="element-403">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-404">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-405">
                        {hour.label}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-406">
                      {hour.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-407">Sin datos</p>}
          </CardContent>
        </Card>

        {/* QR Self-service */}
        <Card data-id="element-408">
          <CardContent className="p-5" data-id="element-409">
            <div className="flex items-center gap-2 mb-3" data-id="element-410">
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-411">
                <CalendarIcon className="w-4 h-4 text-emerald-600" data-id="element-412" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-413">
                Autoservicio QR
              </span>
            </div>
            <div className="space-y-1" data-id="element-414">
              <p className="text-2xl font-bold text-emerald-600" data-id="element-415">
                {channelBreakdown.qr}
              </p>
              <p className="text-sm text-gray-500" data-id="element-416">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-417">{qrPct}%</span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-418">
                {formatCurrency(channelBreakdown.qrRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Waiter */}
        <Card data-id="element-419">
          <CardContent className="p-5" data-id="element-420">
            <div className="flex items-center gap-2 mb-3" data-id="element-421">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-422">
                <UsersIcon className="w-4 h-4 text-blue-600" data-id="element-423" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-424">
                Mesero
              </span>
            </div>
            <div className="space-y-1" data-id="element-425">
              <p className="text-2xl font-bold text-blue-600" data-id="element-426">
                {channelBreakdown.waiter}
              </p>
              <p className="text-sm text-gray-500" data-id="element-427">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-428">
                  {waiterPct}%
                </span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-429">
                {formatCurrency(channelBreakdown.waiterRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}